/**
 * Print tags - configuration, preview and the duplicate guard (brief §25–§28).
 *
 * ONE STEP, NOT A WIZARD (§25 says so outright): scope, services and copies sit
 * above a live preview of the real print HTML, and the button says how many tags
 * will come off the roll. An operator runs this dozens of times a day; a
 * three-screen flow would be paid every time.
 *
 * The preview renders the ACTUAL markup in an iframe rather than a React
 * lookalike, so what is approved here is what the printer receives. It cannot
 * show the 50mm page geometry - a browser cannot preview a thermal page - which
 * is precisely why the page CSS is left alone and the physical first sheet is
 * checked on the TTP-244 itself.
 *
 * §28: tags are physical. Two identical WI-1/20 tags on the sorting table is a
 * garment mix-up, not a wasted label, so a run that includes anything already
 * printed stops and asks first.
 */
import { useMemo, useState } from 'react'
import { AlertTriangle, Printer } from 'lucide-react'

import { useColors } from '../../../hooks/useColors'
import { Modal, PillButton } from '../components/primitives'
import { serviceLabel as catalogueLabel } from '../serviceCatalogue'
import { buildTagHtml, jobTagCount, openTagJob } from './b2bRoomDocs'
import { PRINT_SCOPES, buildTagPlan, scopeRoomCounts, servicesInCycle } from './printScope'
import type { PrintScope } from './printScope'
import type { B2bOrderRecord, B2bRoomRecord, B2bServiceCatalogueEntry } from '../../../services/b2bApi'

/**
 * Preview-only CSS. Deliberately NOT the print stylesheet: that one is in
 * millimetres against a 50mm page, and inside a 150px preview box it would
 * render at the wrong scale and teach the operator to expect the wrong layout.
 * This shows the same ELEMENTS in the same order at screen sizes.
 */
const TAG_PREVIEW_CSS = `
  body { margin: 0; font-family: Arial, sans-serif; color: #000; background: #fff; }
  .tag { width: 100%; height: 100%; padding: 8px; display: flex; flex-direction: column;
    align-items: center; justify-content: center; gap: 3px; text-align: center; }
  .brand { font-size: 10px; font-weight: 800; letter-spacing: .5px; }
  .client { font-size: 11px; font-weight: 700; }
  .room-label { font-size: 9px; letter-spacing: 1px; text-transform: uppercase; }
  .room { font-size: 30px; font-weight: 800; line-height: 1; }
  .seq { font-size: 22px; font-weight: 800; }
  .svc { font-size: 11px; font-weight: 700; }
  .row { font-size: 10px; }
`

const wrap = (html: string) =>
  `<html><head><meta charset="utf-8"/><style>${TAG_PREVIEW_CSS}</style></head><body>${html}</body></html>`

const MAX_PREVIEW_TAGS = 3
const MAX_LISTED_ROOMS = 8

export interface PrintTagsModalProps {
  clientName: string
  cycleLabel: string
  rooms: B2bRoomRecord[]
  orders: B2bOrderRecord[]
  selectedRoomIds: ReadonlySet<string>
  catalogue: B2bServiceCatalogueEntry[]
  /** Where the dialog opens: the row menu scopes to one room, the toolbar does not. */
  initialScope?: PrintScope
  onClose: () => void
  /** Stamp printed_at. Called after the print window has been handed the job. */
  onPrinted: (orderIds: string[]) => void
}

export default function PrintTagsModal({
  clientName,
  cycleLabel,
  rooms,
  orders,
  selectedRoomIds,
  catalogue,
  initialScope = 'selected',
  onClose,
  onPrinted,
}: PrintTagsModalProps) {
  const colors = useColors()

  const labelFor = useMemo(() => {
    const byslug = new Map(catalogue.map((entry) => [entry.slug, entry.label]))
    return (slug: string) => byslug.get(slug) ?? catalogueLabel(slug)
  }, [catalogue])

  const availableServices = useMemo(
    () => servicesInCycle(orders, labelFor),
    [orders, labelFor],
  )

  const [scope, setScope] = useState<PrintScope>(initialScope)
  const [services, setServices] = useState<ReadonlySet<string>>(
    () => new Set(availableServices.map((service) => service.slug)),
  )
  const [copies, setCopies] = useState('1')

  const copiesValue = Math.max(1, Math.min(9, Math.floor(Number(copies)) || 1))

  const plan = useMemo(
    () =>
      buildTagPlan({
        rooms,
        orders,
        selectedRoomIds,
        services,
        scope,
        clientName,
        copies: copiesValue,
      }),
    [rooms, orders, selectedRoomIds, services, scope, clientName, copiesValue],
  )

  const counts = useMemo(
    () => scopeRoomCounts({ rooms, orders, selectedRoomIds, services, clientName }),
    [rooms, orders, selectedRoomIds, services, clientName],
  )

  const previewTags = useMemo(
    () =>
      plan.rooms
        .flatMap((room) => room.tags.map((tag) => ({ meta: room.meta, tag })))
        .slice(0, MAX_PREVIEW_TAGS),
    [plan],
  )

  const toggleService = (slug: string) => {
    setServices((current) => {
      const next = new Set(current)
      if (next.has(slug)) next.delete(slug)
      else next.add(slug)
      return next
    })
  }

  /**
   * §28's question is asked by the banner above the button, not by a second
   * press. The warning is already on screen and the button has already changed
   * its own label to "Reprint" - making the operator click twice would add a
   * step to a dialog the brief explicitly says must not become a wizard, and
   * teach them to click through it.
   */
  const isReprint = plan.reprintOrderIds.length > 0

  const doPrint = () => {
    if (plan.tagCount === 0) return
    openTagJob(plan.rooms, copiesValue, `${clientName}. ${plan.tagCount} piece tags`)
    onPrinted(plan.orderIds)
    onClose()
  }

  const sectionLabel = {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.6px',
    textTransform: 'uppercase' as const,
    color: colors.textMuted,
    marginBottom: '8px',
  }

  return (
    <Modal
      title="Print tags"
      onClose={onClose}
      width="640px"
      footer={
        <>
          <PillButton variant="secondary" onClick={onClose}>
            Cancel
          </PillButton>
          <PillButton
            onClick={doPrint}
            disabled={plan.tagCount === 0}
            title={plan.tagCount === 0 ? 'Nothing in this selection to tag' : undefined}
          >
            <Printer size={14} />
            {plan.tagCount === 0
              ? 'Nothing to print'
              : `${isReprint ? 'Reprint' : 'Print'} ${plan.tagCount} ${
                  plan.tagCount === 1 ? 'tag' : 'tags'
                }`}
          </PillButton>
        </>
      }
    >
      <div style={{ display: 'grid', gap: '18px' }}>
        <div className="font-body" style={{ fontSize: '12px', color: colors.textSecondary }}>
          <strong style={{ color: colors.textPrimary }}>{clientName}</strong>
          <span> · {cycleLabel}</span>
        </div>

        {/* ── §28 duplicate guard ─────────────────────────────────── */}
        {plan.reprintOrderIds.length > 0 ? (
          <div
            role="alert"
            data-testid="reprint-warning"
            style={{
              display: 'flex',
              gap: '10px',
              padding: '10px 12px',
              borderRadius: '10px',
              border: '1px solid rgba(180,83,9,0.35)',
              background: 'rgba(180,83,9,0.08)',
            }}
          >
            <AlertTriangle size={16} color="var(--chip-warning-ink)" style={{ flexShrink: 0, marginTop: '1px' }} />
            <div className="font-body" style={{ fontSize: '12px', color: colors.textPrimary }}>
              <div style={{ fontWeight: 700 }}>These tags have already been printed.</div>
              <div style={{ color: colors.textSecondary, marginTop: '2px' }}>
                Would you like to reprint them? The originals stay on the garments, so you will
                have two sets - Cancel if that is not what you meant.
              </div>
            </div>
          </div>
        ) : null}

        {/* ── scope ───────────────────────────────────────────────── */}
        <fieldset style={{ border: 'none', margin: 0, padding: 0 }}>
          <legend className="font-body" style={sectionLabel}>
            Print
          </legend>
          <div style={{ display: 'grid', gap: '6px' }}>
            {PRINT_SCOPES.map((option) => (
              <label
                key={option.value}
                className="font-body"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  name="print-scope"
                  value={option.value}
                  checked={scope === option.value}
                  onChange={() => setScope(option.value)}
                  style={{ accentColor: colors.textPrimary }}
                />
                <span>{option.label}</span>
                <span style={{ fontSize: '11px', color: colors.textMuted }}>
                  {counts[option.value]} room{counts[option.value] === 1 ? '' : 's'}
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        {/* ── services ────────────────────────────────────────────── */}
        <fieldset style={{ border: 'none', margin: 0, padding: 0 }}>
          <legend className="font-body" style={sectionLabel}>
            Services
          </legend>
          {availableServices.length === 0 ? (
            <p className="font-body" style={{ margin: 0, fontSize: '12px', color: colors.textMuted }}>
              Nothing has been billed in this cycle yet.
            </p>
          ) : (
            <div style={{ display: 'grid', gap: '6px' }}>
              {availableServices.map((service) => (
                <label
                  key={service.slug}
                  className="font-body"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={services.has(service.slug)}
                    onChange={() => toggleService(service.slug)}
                    style={{ accentColor: colors.textPrimary }}
                  />
                  <span>{service.label}</span>
                </label>
              ))}
            </div>
          )}
        </fieldset>

        {/* ── copies ──────────────────────────────────────────────── */}
        <label className="font-body" style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px' }}>
          <span style={{ fontWeight: 600 }}>Copies</span>
          <input
            type="number"
            min={1}
            max={9}
            value={copies}
            onChange={(event) => setCopies(event.target.value)}
            aria-label="Copies of each tag"
            style={{
              width: '64px',
              height: '32px',
              padding: '0 8px',
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
              background: colors.bgCard,
              color: colors.textPrimary,
              fontSize: '13px',
            }}
          />
          <span style={{ fontSize: '11px', color: colors.textMuted }}>of every tag</span>
        </label>

        {/* ── what is being skipped, and why ──────────────────────── */}
        {plan.blocked.length > 0 ? (
          <div
            data-testid="blocked-services"
            style={{
              padding: '10px 12px',
              borderRadius: '10px',
              border: `1px solid ${colors.border}`,
              background: colors.surface,
            }}
          >
            <div className="font-body" style={{ fontSize: '12px', fontWeight: 700, marginBottom: '4px' }}>
              Not printing {plan.blocked.length} service{plan.blocked.length === 1 ? '' : 's'}
            </div>
            <ul
              className="font-body"
              style={{ margin: 0, paddingLeft: '16px', fontSize: '12px', color: colors.textSecondary }}
            >
              {plan.blocked.slice(0, 5).map((entry, index) => (
                <li key={`${entry.roomNumber}-${entry.blocker.serviceSlug}-${index}`}>
                  Room {entry.roomNumber}: {entry.blocker.reason}
                </li>
              ))}
              {plan.blocked.length > 5 ? <li>…and {plan.blocked.length - 5} more.</li> : null}
            </ul>
          </div>
        ) : null}

        {/* ── preview ─────────────────────────────────────────────── */}
        <div>
          <div className="font-body" style={sectionLabel}>
            Preview
          </div>

          <p
            className="font-body"
            data-testid="tag-count"
            style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 700 }}
          >
            {plan.tagCount} tag{plan.tagCount === 1 ? '' : 's'} ready to print
            <span style={{ fontWeight: 500, color: colors.textSecondary }}>
              {' '}
              · {plan.roomCount} room{plan.roomCount === 1 ? '' : 's'}
              {copiesValue > 1 ? ` · ${copiesValue} copies each` : ''}
            </span>
          </p>

          {plan.roomCount > 0 ? (
            <p
              className="font-body"
              data-testid="tag-rooms"
              style={{ margin: '0 0 10px', fontSize: '12px', color: colors.textSecondary }}
            >
              Rooms:{' '}
              {plan.rooms
                .slice(0, MAX_LISTED_ROOMS)
                .map((room) => room.roomNumber)
                .join(', ')}
              {plan.roomCount > MAX_LISTED_ROOMS ? ` +${plan.roomCount - MAX_LISTED_ROOMS} more` : ''}
            </p>
          ) : null}

          {previewTags.length === 0 ? (
            <p className="font-body" style={{ margin: 0, fontSize: '13px', color: colors.textMuted }}>
              Nothing in this selection to tag. Choose another scope, or tick a service.
            </p>
          ) : (
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {previewTags.map(({ meta, tag }) => (
                <iframe
                  key={`${meta.roomNumber}-${tag.serviceSlug}-${tag.sequence}`}
                  title={`Tag ${tag.label} for room ${meta.roomNumber}`}
                  srcDoc={wrap(buildTagHtml(meta, tag))}
                  style={{
                    width: '150px',
                    height: '210px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '10px',
                    background: '#fff',
                  }}
                />
              ))}
              {plan.pieceCount > previewTags.length ? (
                <div
                  className="font-body"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '12px',
                    color: colors.textMuted,
                  }}
                >
                  +{jobTagCount(plan.rooms) - previewTags.length} more
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}
